<ng-template let-extraClass="extraClass" #topNav>
  @if (!windowService.isDesktopRes) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="'Leave Guest Review'"
      [canGoBack]="true"
      [customBackAction]="topNavBack" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #content>
  <tidy-wrapper [class]="windowService.isDesktopRes ? 'right-side-scroll-enabled' : ''">
    @if (loading) {
      <tidy-loader [show]="true" />
    } @else if (expected) {
      <tidy-card class="leave-review__hero">
        <span class="platform-chip" [ngClass]="platformChipClass">{{ channelLabel }}</span>
        <div class="leave-review__hero-body">
          <div class="leave-review__hero-avatar">
            <tidy-initials-icon [fullName]="expected.guestName || 'Guest'" />
          </div>
          <tidy-text
            [title]="expected.guestName || 'Guest'"
            [translate]="false" />
          @if (expected.listingName) {
            <tidy-text
              [helper]="('Stayed at ' | translate) + expected.listingName"
              [translate]="false" />
          }
          @if (stayDates) {
            <div class="leave-review__hero-meta">
              <tidy-image [src]="'assets/svg/calendar-outline.svg'" class="leave-review__meta-icon" />
              <tidy-text
                [helper]="stayDates"
                [translate]="false" />
            </div>
          }
        </div>
      </tidy-card>

      @if (isQueued && leaveAvailable) {
        <div class="leave-review__queued-banner">
          <tidy-image [src]="'assets/svg/time-outline.svg'" class="leave-review__queued-icon" />
          <tidy-text
            [body]="'Queued — auto-posts on ' + queuedPostDate"
            [translate]="false" />
        </div>
      }

      @if (errorMessage) {
        <tidy-error-message [text]="errorMessage" />
      }

      @if (mode === 'posted') {
        <tidy-card>
          <tidy-row>
            <tidy-text [title]="'Review Posted'" />
          </tidy-row>
          <tidy-row class="extra-top-padding">
            <tidy-text [helper]="'You have already left a review for this guest.'" />
          </tidy-row>
          @if (existing?.public_text) {
            <tidy-row class="extra-top-padding">
              <tidy-text
                [body]="existing.public_text"
                [translate]="false" />
            </tidy-row>
          }
        </tidy-card>
      } @else if (!leaveAvailable) {
        <tidy-card>
          <tidy-row>
            <tidy-text [title]="'Leave a Review'" />
          </tidy-row>
          <tidy-row>
            <tidy-text
              [helper]="leaveUnavailableMessage"
              [translate]="false" />
          </tidy-row>
        </tidy-card>
        @if (existing) {
          <tidy-row [align]="'center'" class="extra-top-padding">
            <tidy-text
              [actionBody]="isQueued ? 'Cancel Queued Review' : 'Cancel Review'"
              (action)="cancelQueued()"
              class="delete-automation-link" />
          </tidy-row>
        }
      } @else {
        <form [formGroup]="form" novalidate action="#">
          <tidy-card>
            <tidy-row>
              <tidy-text [title]="'Rate this Guest'" />
            </tidy-row>
            @for (category of categories; track category.key) {
              <div class="leave-review__rating-row">
                <div class="leave-review__rating-info">
                  <tidy-text
                    [body]="category.label"
                    [translate]="false" />
                  <span class="leave-review__rating-word" [ngClass]="'leave-review__rating-word--' + ratingMeta[category.key].cls">
                    {{ ratingMeta[category.key].word }}
                  </span>
                </div>
                <star-rating
                  [value]="ratings[category.key]"
                  (valueChange)="setRating(category.key, $event)" />
              </div>
            }
          </tidy-card>

          <tidy-card>
            <tidy-row>
              <tidy-text [title]="'Would you host this guest again?'" />
            </tidy-row>
            <div class="leave-review__recommend-grid">
              <div class="recommend-card recommend-card--yes"
                   [class.recommend-card--selected]="recommend"
                   (click)="selectRecommend(true)">
                <tidy-image [src]="'assets/svg/thumbs-up-outline.svg'" class="recommend-card__icon" />
                <tidy-text [body]="'Yes, I\'d host again'" />
              </div>
              <div class="recommend-card recommend-card--no"
                   [class.recommend-card--selected]="!recommend"
                   (click)="selectRecommend(false)">
                <tidy-image [src]="'assets/svg/thumbs-down-outline.svg'" class="recommend-card__icon" />
                <tidy-text [body]="'No, I wouldn\'t'" />
              </div>
            </div>
          </tidy-card>

          <tidy-card>
            <tidy-row>
              <tidy-text [title]="'Public Review'" />
            </tidy-row>
            <tidy-row>
              <tidy-text [helper]="'Shown on the guest\'s profile.'" />
            </tidy-row>
            <tidy-textarea
              [label]="'Public review'"
              formControlName="publicText"
              [form]="form.controls.publicText"
              [icon]="'assets/svg/create-outline.svg'"
              [submitted]="submitted"
              [errorMessage]="'Please write a public review'" />
          </tidy-card>

          <tidy-card>
            <tidy-row>
              <tidy-text [title]="'Private Feedback'" />
            </tidy-row>
            <tidy-row>
              <tidy-text [helper]="'Only the guest sees this. Optional.'" />
            </tidy-row>
            <tidy-textarea
              [label]="'Private feedback'"
              formControlName="privateNote"
              [form]="form.controls.privateNote"
              [icon]="'assets/svg/lock-closed.svg'" />
          </tidy-card>
        </form>

        @if (mode === 'create') {
          <tidy-card class="leave-review__paths">
            <tidy-row>
              <tidy-text [title]="'How posting works'" />
            </tidy-row>
            <div class="leave-review__path-row">
              <div class="leave-review__path-icon leave-review__path-icon--now">
                <tidy-image [src]="'assets/svg/flash-outline.svg'" />
              </div>
              <div class="leave-review__path-text">
                <tidy-text [body]="'Post Now'" />
                <tidy-text
                  [helper]="'Publishes to ' + channelLabel + ' right away. Best for positive reviews.'"
                  [translate]="false" />
              </div>
            </div>
            <div class="leave-review__path-row">
              <div class="leave-review__path-icon leave-review__path-icon--queue">
                <tidy-image [src]="'assets/svg/time-outline.svg'" />
              </div>
              <div class="leave-review__path-text">
                <tidy-text [body]="'Queue as Negative'" />
                <tidy-text
                  [helper]="'Auto-posts on ' + negativePostDate + ' — the end of the 14-day window — leaving the guest no time to retaliate.'"
                  [translate]="false" />
              </div>
            </div>
          </tidy-card>
        }

        @if (isQueued) {
          <tidy-button
            [text]="submitting ? 'Saving…' : 'Update Queued Review'"
            [action]="queueNegative"
            class="primary"
            [disabled]="submitting" />
          <tidy-row [align]="'center'" class="extra-top-padding">
            <tidy-text
              [actionBody]="'Cancel Queued Review'"
              (action)="cancelQueued()"
              class="delete-automation-link" />
          </tidy-row>
        } @else if (isRetryable) {
          <tidy-button
            [text]="submitting ? 'Posting…' : 'Retry Posting'"
            [action]="postNow"
            class="primary"
            [disabled]="submitting" />
          <tidy-row [align]="'center'" class="extra-top-padding">
            <tidy-text
              [actionBody]="'Cancel Review'"
              (action)="cancelQueued()"
              class="delete-automation-link" />
          </tidy-row>
        } @else {
          <tidy-button
            [text]="submitting ? 'Posting…' : 'Post Now'"
            [action]="postNow"
            class="primary"
            [disabled]="submitting" />
          <tidy-button
            [text]="'Queue as Negative'"
            [action]="queueNegative"
            class="secondary"
            [disabled]="submitting" />
        }
      }
    }
  </tidy-wrapper>
</ng-template>

@if (windowService.isDesktopRes) {
  <ng-container *ngTemplateOutlet="content" />
} @else {
  <app-scroll-wrapper [templateContent]="content" />
}
